import { YStack } from '@mythical/ui'
import {
  LandingCollectibles as LandingCollectiblesType,
  LandingCollectibleItem,
} from 'app/blocks/landing/landing-collectibles'
import { ButtonLink } from 'app/components/primitive/Button'
import { useStores } from 'app/store'
import React, { useCallback, useMemo } from 'react'
import { MarketingHeading } from 'app/features/landing/components'
import { Container } from 'app/components/structure/Container'
import {
  ContentCarousel,
  type SizeConfig,
} from 'app/components/content/ContentCarousel'
import { CollectThemAllCard } from './CollectThemAllCard'

const sizeConfig: SizeConfig = {
  width: 277,
  spacing: 16,
  $gtXs: { width: 277, spacing: 16 },
  $gtSm: { width: 450, spacing: 16 },
}

function toDate(value?: string | null): Date | null {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function getQuarterEndDate(quarter?: string): Date | null {
  if (!quarter) return null

  const match = quarter.toUpperCase().match(/Q([1-4])\s+(\d{4})/)
  if (!match) return null

  const quarterIndex = Number(match[1])
  const year = Number(match[2])
  if (!quarterIndex || !year) return null

  // Day 0 of the next quarter month gives us the final day of the current quarter.
  return new Date(Date.UTC(year, quarterIndex * 3, 0, 23, 59, 59, 999))
}

function inferCollectiblesAvailability(
  collectibles: LandingCollectibleItem[],
): LandingCollectibleItem[] {
  const collectiblesWithStartDate = collectibles
    .map((item, index) => ({
      index,
      startDate: toDate(item.availableFrom),
    }))
    .filter((entry) => Boolean(entry.startDate))
    .sort((a, b) => {
      return (
        (a.startDate?.getTime() ?? Number.MAX_SAFE_INTEGER) -
        (b.startDate?.getTime() ?? Number.MAX_SAFE_INTEGER)
      )
    })

  const nextStartDateByIndex = new Map<number, Date>()
  collectiblesWithStartDate.forEach((entry, index) => {
    const nextEntry = collectiblesWithStartDate[index + 1]
    if (nextEntry?.startDate) {
      nextStartDateByIndex.set(entry.index, nextEntry.startDate)
    }
  })

  const explicitEndDates = collectibles
    .map((item) => toDate(item.availableUntil))
    .filter((date): date is Date => Boolean(date))

  const quarterEndDates = collectibles
    .map((item) => getQuarterEndDate(item.quarter))
    .filter((date): date is Date => Boolean(date))

  const roadmapEndDate =
    explicitEndDates.length > 0
      ? new Date(Math.max(...explicitEndDates.map((date) => date.getTime())))
      : quarterEndDates.length > 0
        ? new Date(Math.max(...quarterEndDates.map((date) => date.getTime())))
        : null

  return collectibles.map((item, index) => {
    if (item.availableUntil) {
      return item
    }

    const itemStartDate = toDate(item.availableFrom)
    const nextStartDate = nextStartDateByIndex.get(index)

    let inferredUntilDate: Date | null = null
    if (nextStartDate) {
      inferredUntilDate = new Date(nextStartDate.getTime() - 1)
    } else if (roadmapEndDate) {
      inferredUntilDate = roadmapEndDate
    }

    const hasValidWindow =
      inferredUntilDate &&
      (!itemStartDate || inferredUntilDate.getTime() >= itemStartDate.getTime())

    return {
      ...item,
      ...(hasValidWindow
        ? { availableUntil: inferredUntilDate?.toISOString() }
        : {}),
    }
  })
}

interface CollectThemAllBlockProps {
  block: LandingCollectiblesType
}

export const CollectThemAllBlock: React.FC<CollectThemAllBlockProps> = ({
  block,
}) => {
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  if (!block.collectibles || block.collectibles.length === 0) return null

  const items = useMemo(
    () => inferCollectiblesAvailability(block.collectibles),
    [block.collectibles],
  )

  const renderItem = useCallback((item: LandingCollectibleItem) => {
    return <CollectThemAllCard item={item} />
  }, [])

  const keyExtractor = useCallback(
    (item: LandingCollectibleItem) => item.id,
    [],
  )

  return (
    <Container variant="none" paddedX gap="$5" $gtSm={{ gap: '$8.5' }}>
      <YStack gap="$3.5" jc="center" ai="center" $gtSm={{ gap: '$5' }}>
        {block.title && (
          <MarketingHeading tag="h2" ta="center" mx="auto">
            {block.title}
          </MarketingHeading>
        )}
        {canMemberfulLink && (
          <ButtonLink href="/perks">Join All Access to Unlock</ButtonLink>
        )}
      </YStack>

      <YStack $gtSm={{ overflow: 'hidden' }}>
        <ContentCarousel
          items={items}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          sizeConfig={sizeConfig}
        />
      </YStack>
    </Container>
  )
}
